import { useState } from "react";
import { useTheme } from "styled-components";
import { ColorPicker } from "./color-picker";
import { ModeButton, SidebarLabel } from "./ui";
import { SHEETS, STAMPS, FLOOR_TILES, WALL_TILES } from "../lib/tilesets";

const OPACITY_STEPS = [0.25, 0.5, 0.75, 1];

// Sheets grouped under each picker tab. "Floors & Walls" leads with the named
// FLOOR_TILES/WALL_TILES swatches, then lets you click any cell on those same
// sheets; "Furniture" is the curated STAMPS list; "Props" is raw single-cell
// picking across the remaining sheets (food, kitchenware, small decor).
const TABS = {
  floors: { label: "Floors & Walls", sheets: ["bar3", "bar5", "mb-walls-2", "mb-walls-3"] },
  furniture: { label: "Furniture", sheets: [] },
  props: { label: "Props", sheets: ["bar1", "bar2", "bar4", "bar6", "mb1", "mb2", "mb3", "mb4", "mb5"] },
};

const DISPLAY_SCALE = 0.5;

// A scaled, clickable render of one full sheet — click any cell to select it.
const SheetPicker = ({ sheetId, selectedBrush, onPick, borderColor, activeColor }) => {
  const sheet = SHEETS[sheetId];
  if (!sheet) return null;
  const cellSize = sheet.tileSize * DISPLAY_SCALE;
  const dispW = sheet.cols * cellSize;
  const dispH = sheet.rows * cellSize;

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tx = Math.floor((e.clientX - rect.left) / cellSize);
    const ty = Math.floor((e.clientY - rect.top) / cellSize);
    onPick({ kind: "flat", sheet: sheetId, tx, ty });
  };

  const isSelected =
    selectedBrush.kind === "flat" &&
    selectedBrush.sheet === sheetId;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: dispW,
        maxHeight: 260,
        overflow: "auto",
        border: `1px solid ${borderColor}`,
        marginBottom: "0.5rem",
      }}
    >
      <div
        onClick={handleClick}
        style={{
          position: "relative",
          width: dispW,
          height: dispH,
          backgroundImage: `url("${encodeURI(sheet.src)}")`,
          backgroundSize: `${dispW}px ${dispH}px`,
          cursor: "crosshair",
          imageRendering: "pixelated",
        }}
      >
        {isSelected && (
          <div
            style={{
              position: "absolute",
              left: selectedBrush.tx * cellSize,
              top: selectedBrush.ty * cellSize,
              width: cellSize,
              height: cellSize,
              border: `2px solid ${activeColor}`,
              boxSizing: "border-box",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </div>
  );
};

// Fixed on-screen size (px) of one tile in a Swatch preview — kept constant across every
// swatch so a 1x1 stool and an 8x2 bar counter render at their true relative footprint
// instead of both being scaled to fit the same box.
const SWATCH_TILE_PX = 28;

// Small cropped preview swatch for a named flat pick or stamp, shown at actual (relative)
// size: an item w tiles wide renders w * SWATCH_TILE_PX px wide, same for height.
const Swatch = ({ sheetId, tx, ty, w = 1, h = 1, label, active, onClick, activeColor, borderColor }) => {
  const sheet = SHEETS[sheetId];
  if (!sheet) return null;
  const cellSize = SWATCH_TILE_PX;
  return (
    <div
      title={label}
      onClick={onClick}
      style={{
        width: cellSize * w,
        height: cellSize * h,
        backgroundImage: `url("${encodeURI(sheet.src)}")`,
        backgroundSize: `${sheet.cols * cellSize}px ${sheet.rows * cellSize}px`,
        backgroundPosition: `-${tx * cellSize}px -${ty * cellSize}px`,
        border: `2px solid ${active ? activeColor : borderColor}`,
        cursor: "pointer",
        flexShrink: 0,
        imageRendering: "pixelated",
      }}
    />
  );
};

// Shared paint sidebar for the tiles / decoration / overlay modes.
// Renders (in order, each section only when its props are given):
//   hint text, solid checkbox, erase checkbox, opacity row,
//   color/tileset toggle, color picker or tileset picker.
export const PaintControls = ({
  hint,
  solid,
  onSolidChange,
  erase,
  onEraseChange,
  opacity,
  onOpacityChange,
  tileMode,
  setTileMode,
  color,
  setColor,
  isPickingColor,
  onStartPick,
  selectedBrush,
  setSelectedBrush,
  patternSize,
  setPatternSize,
  floodFill,
  setFloodFill,
  flipX,
  setFlipX,
  copyMode,
  setCopyMode,
  clipboard,
}) => {
  const { colors } = useTheme();
  const text = colors("highlight");
  const muted = colors("highlight", 0.55);
  const border = colors("highlight", 0.3);
  const [tab, setTab] = useState("floors");
  const [sheetIndex, setSheetIndex] = useState(0);
  const changeTab = (id) => {
    setTab(id);
    setSheetIndex(0);
  };

  const checkbox = (label, checked, onChange) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
        fontSize: "0.65rem",
        marginBottom: "0.6rem",
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );

  const namedFlats = tab === "floors" ? [...FLOOR_TILES, ...WALL_TILES] : [];

  return (
    <>
      {setCopyMode && (
        <div style={{ marginBottom: "0.6rem" }}>
          {checkbox("Copy (drag to select a region)", copyMode, setCopyMode)}
          {clipboard && !copyMode && (
            <div style={{ fontSize: "0.6rem", color: muted, marginTop: "-0.3rem", marginBottom: "0.4rem" }}>
              Clipboard: {clipboard.w}x{clipboard.h} — click the map to paste
            </div>
          )}
        </div>
      )}
      {hint && (
        <div style={{ fontSize: "0.62rem", color: muted, marginBottom: "0.75rem" }}>
          {hint}
        </div>
      )}
      {onSolidChange && checkbox("Solid (wall)", solid, onSolidChange)}
      {onEraseChange && checkbox("Erase", erase, onEraseChange)}
      {setFloodFill && checkbox("Flood fill (click to fill area)", floodFill, setFloodFill)}
      {!erase && (
        <>
          {onOpacityChange && (
            <>
              <SidebarLabel>Opacity</SidebarLabel>
              <div style={{ display: "flex", gap: "0.25rem", marginBottom: "0.75rem" }}>
                {OPACITY_STEPS.map((v) => (
                  <ModeButton
                    key={v}
                    $active={opacity === v}
                    onClick={() => onOpacityChange(v)}
                    style={{ flex: 1, fontSize: "0.6rem" }}
                  >
                    {v * 100}%
                  </ModeButton>
                ))}
              </div>
            </>
          )}
          <div style={{ display: "flex", gap: "0.25rem", marginBottom: "0.5rem" }}>
            <ModeButton
              $active={tileMode === "color"}
              onClick={() => setTileMode("color")}
              style={{ flex: 1, fontSize: "0.6rem" }}
            >
              Color
            </ModeButton>
            <ModeButton
              $active={tileMode === "tileset"}
              onClick={() => setTileMode("tileset")}
              style={{ flex: 1, fontSize: "0.6rem" }}
            >
              Tileset
            </ModeButton>
          </div>
          {tileMode === "color" && (
            <ColorPicker
              color={color}
              onChange={setColor}
              isPicking={isPickingColor}
              onStartPick={onStartPick}
            />
          )}
          {tileMode === "tileset" && (
            <>
              <div style={{ display: "flex", gap: "0.25rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                {Object.entries(TABS).map(([id, t]) => (
                  <ModeButton
                    key={id}
                    $active={tab === id}
                    onClick={() => changeTab(id)}
                    style={{ flex: "1 0 auto", fontSize: "0.55rem" }}
                  >
                    {t.label}
                  </ModeButton>
                ))}
              </div>

              {setFlipX && checkbox("Flip horizontally", flipX, setFlipX)}

              {tab !== "furniture" && setPatternSize && (
                <>
                  <SidebarLabel>Pattern ({patternSize.w}x{patternSize.h})</SidebarLabel>
                  <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.5rem" }}>
                    {[
                      { axis: "w", label: "X" },
                      { axis: "h", label: "Y" },
                    ].map(({ axis, label }) => (
                      <div key={axis} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                        <span style={{ fontSize: "0.6rem", color: muted }}>{label}</span>
                        <ModeButton
                          onClick={() => setPatternSize((p) => ({ ...p, [axis]: Math.max(1, p[axis] - 1) }))}
                          style={{ fontSize: "0.6rem", padding: "0.2rem 0.5rem" }}
                        >
                          −
                        </ModeButton>
                        <span style={{ fontSize: "0.65rem", minWidth: "12px", textAlign: "center" }}>
                          {patternSize[axis]}
                        </span>
                        <ModeButton
                          onClick={() => setPatternSize((p) => ({ ...p, [axis]: Math.min(8, p[axis] + 1) }))}
                          style={{ fontSize: "0.6rem", padding: "0.2rem 0.5rem" }}
                        >
                          +
                        </ModeButton>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {tab === "furniture" ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.4rem" }}>
                  {STAMPS.map((s) => (
                    <Swatch
                      key={s.id}
                      sheetId={s.sheet}
                      tx={s.tx}
                      ty={s.ty}
                      w={s.w}
                      h={s.h}
                      label={`${s.label} (${s.w}x${s.h})`}
                      active={selectedBrush.kind === "stamp" && selectedBrush.stampId === s.id}
                      activeColor={text}
                      borderColor={border}
                      onClick={() => setSelectedBrush({ kind: "stamp", stampId: s.id })}
                    />
                  ))}
                </div>
              ) : (
                <>
                  {namedFlats.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.5rem" }}>
                      {namedFlats.map((f) => (
                        <Swatch
                          key={f.id}
                          sheetId={f.sheet}
                          tx={f.tx}
                          ty={f.ty}
                          label={f.label}
                          active={selectedBrush.kind === "flat" && selectedBrush.sheet === f.sheet && selectedBrush.tx === f.tx && selectedBrush.ty === f.ty}
                          activeColor={text}
                          borderColor={border}
                          onClick={() => setSelectedBrush({ kind: "flat", sheet: f.sheet, tx: f.tx, ty: f.ty })}
                        />
                      ))}
                    </div>
                  )}
                  {(() => {
                    const sheets = TABS[tab].sheets;
                    const idx = Math.min(sheetIndex, sheets.length - 1);
                    const sheetId = sheets[idx];
                    return (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                          <ModeButton
                            onClick={() => setSheetIndex((idx - 1 + sheets.length) % sheets.length)}
                            style={{ fontSize: "0.6rem", padding: "0.25rem 0.5rem" }}
                            disabled={sheets.length < 2}
                          >
                            ‹
                          </ModeButton>
                          <SidebarLabel style={{ margin: 0 }}>
                            {SHEETS[sheetId]?.label} ({idx + 1}/{sheets.length})
                          </SidebarLabel>
                          <ModeButton
                            onClick={() => setSheetIndex((idx + 1) % sheets.length)}
                            style={{ fontSize: "0.6rem", padding: "0.25rem 0.5rem" }}
                            disabled={sheets.length < 2}
                          >
                            ›
                          </ModeButton>
                        </div>
                        <SheetPicker
                          sheetId={sheetId}
                          selectedBrush={selectedBrush}
                          onPick={setSelectedBrush}
                          borderColor={border}
                          activeColor={text}
                        />
                      </div>
                    );
                  })()}
                </>
              )}
              <div style={{ fontSize: "0.6rem", color: muted, marginTop: "0.2rem" }}>
                {selectedBrush.kind === "stamp"
                  ? STAMPS.find((s) => s.id === selectedBrush.stampId)?.label
                  : selectedBrush.kind === "flat"
                    ? `${SHEETS[selectedBrush.sheet]?.label ?? selectedBrush.sheet} (${selectedBrush.tx},${selectedBrush.ty})`
                    : selectedBrush.kind === "clipboard" && clipboard
                      ? `Clipboard (${clipboard.w}x${clipboard.h})`
                      : null}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};
